import {
  SessionType,
  Yakureki,
  YakurekiDetail,
} from '@/shared/types/yakurekiCore'
import { Soaps } from '@/shared/types/yakurekiSoap'
import { YakurekiStatistics } from '@/shared/types/yakurekiStatistics'
import client from '@/shared/apis/client'
import { AxiosPromise } from 'axios'

/**
 * 薬歴詳細情報の取得
 */
export const getYakurekiDetail = (
  yakurekiId: string
): AxiosPromise<YakurekiDetail> => client.get(`/api/yakureki/${yakurekiId}`)

/**
 * 薬歴リストの取得
 *
 * - queue_number: NSIPS 受付番号で完全一致 (= ?queue_number=033 の deep-link 検索)
 * - patient_name: 患者氏名で部分一致 (= AnyCOMPASS iframe からの自動オープン deep-link)
 *   表記揺れ (半角/全角スペース) は backend 側で吸収. 同名複数件は created_at desc の先頭採用.
 * - force_refresh: true で backend の 10s user-scoped cache を bypass
 *   (= deep-link 経由の最新検索など、stale を踏みたくない経路で使う)
 */
export const getYakurekiList = (params?: {
  page?: number
  page_size?: number
  queue_number?: string
  patient_name?: string
  force_refresh?: boolean
}): AxiosPromise<{
  count: number
  results: Yakureki[]
}> => {
  const query: Record<string, string | number> = {}
  if (params?.page !== undefined) query.page = params.page
  if (params?.page_size !== undefined) query.page_size = params.page_size
  if (params?.queue_number !== undefined)
    query.queue_number = params.queue_number
  if (params?.patient_name !== undefined)
    query.patient_name = params.patient_name
  if (params?.force_refresh) query.force_refresh = 1
  return client.get(`/api/yakureki/`, { params: query })
}

/**
 * 薬歴差分データの取得
 */
export const getYakurekiDelta = (
  lastUpdated: string
): AxiosPromise<{
  count: number
  results: Yakureki[]
  last_updated: string
  /** 遠隔の店から見えなくなった処方の id (back log/250) */
  removed_ids?: string[]
}> => client.get(`/api/yakureki/delta/?last_updated=${lastUpdated}`)

/**
 * コピー完了マーク（status → copied に更新）
 */
export const patchCopyDone = (yakurekiId: string): AxiosPromise<Yakureki[]> =>
  client.patch(`/api/yakureki/${yakurekiId}/mark-copied/`)

/**
 * 対応完了マーク（status → dismissed に更新、患者メニューからの消し込み）
 */
export const patchMarkDismissed = (
  yakurekiId: string
): AxiosPromise<{ message: string }> =>
  client.patch(`/api/yakureki/${yakurekiId}/mark-dismissed/`)

/**
 * 対応完了の取り消し（status → copied に戻す）
 */
export const patchUndoDismissed = (
  yakurekiId: string
): AxiosPromise<{ message: string }> =>
  client.patch(`/api/yakureki/${yakurekiId}/undo-dismissed/`)

/**
 * 旧 path: 音声から SOAP を再生成 (= transcribe → SOAP の一気通貫).
 *
 * back の guard: FAILED 状態 + 保存済み音声あり + regenerate_count < 3 でのみ実行可能.
 * FAILED 薬歴の復旧導線 (= 各 app の useRegenerateFromAudioMutation から call).
 */
export const regenerateSoapFromAudio = async (
  yakurekiId: string
): Promise<void> => {
  return client.patch(`/api/yakureki/${yakurekiId}/regenerate/`)
}

/**
 * 新 path: post_batch dictated を input に SOAP のみ再生成 (= LLM 1 call).
 *
 * back の guard: 実行中 (PROCESSING) でない + regenerate_count < 3 + 話者分離版がもうすぐ来る間
 * (running / 音声ありの pending) でない + 会話か資料がある で実行可能 (status は不問).
 * 話者分離版が無ければ今入っている文字起こし (ライブ版) で作り直す.
 * apps/latest 専用 (= apps/latest/features/yakureki/hooks/useRegenerateFromDictatedMutation
 * から call).
 */
export const regenerateSoapFromDictated = async (
  yakurekiId: string
): Promise<void> => {
  return client.patch(`/api/yakureki/${yakurekiId}/regenerate-soap/`)
}

/**
 * NSIPS連携の解除
 */
export const detachNSIPS = async (yakurekiId: string): Promise<void> => {
  return client.patch(`/api/yakureki/${yakurekiId}/detach-nsips/`)
}

/**
 * 来局/在宅 (session_type) の設定 (録音前 idle 時のみ).
 * back は PENDING(録音待ち)の間だけ変更を許可し、録音開始後は 409 で拒否する.
 */
export const patchSessionType = (
  yakurekiId: string,
  sessionType: SessionType
): AxiosPromise<{ session_type: SessionType }> =>
  client.patch(`/api/yakureki/${yakurekiId}/session-type/`, {
    session_type: sessionType,
  })

/**
 * 録音を経ずに空の SOAP を起こして編集可能にする (= 録音スキップ)。
 *
 * back は PENDING かつ SOAP 未作成のときだけ許可し、それ以外は 409 を返す
 * (= 既存の生成物を壊さない)。成功すると status='completed' になり、
 * 詳細画面が Post (SOAP 編集) へ切り替わる。
 */
export const patchManualSoap = (
  yakurekiId: string
): AxiosPromise<YakurekiDetail> =>
  client.patch(`/api/yakureki/${yakurekiId}/manual-soap/`)

/**
 * 録音を経ず、薬剤師メモだけを根拠に SOAP を生成する。
 *
 * manual-soap (空 SOAP を起こすだけ) の上位版。メモに書いた覚書がそのまま SOAP へ
 * 振り分けられる。back は PENDING・SOAP 未作成・メモ 1 件以上のときだけ許可する。
 *
 * 生成は非同期なので即座に status='processing' が返る。以降は詳細画面の
 * polling (refetchInterval) が 'completed' への遷移を拾う。
 * 生成に失敗した場合は status='pending' に戻る (= 録音からやり直せる)。
 */
export const patchGenerateFromMemo = (
  yakurekiId: string
): AxiosPromise<YakurekiDetail> =>
  client.patch(`/api/yakureki/${yakurekiId}/generate-from-memo/`)

/**
 * SOAP情報の更新
 */
export const updateSOAP = async (
  yakurekiId: string,
  soaps: Omit<Soaps, 'id'>,
  editSource?: string
): Promise<void> => {
  return client.patch(`/api/yakureki/${yakurekiId}/soaps/`, {
    soaps,
    edit_source: editSource,
  })
}

/**
 * SOAP編集履歴の取得
 */
export const getSoapEditHistory = (
  yakurekiId: string
): AxiosPromise<SoapEditHistoryItem[]> =>
  client.get(`/api/yakureki/${yakurekiId}/edit-history/`)

/**
 * SOAP編集履歴アイテムの型
 */
export interface SoapEditHistoryItem {
  id: string
  edit_source: string
  edit_source_label: string
  // section key → 差分. edit_source ごとに形状が異なる (= manual/bulk は added/removed/edited、
  // selection_change は deselected/selected、旧 mark_unnecessary は marked_unnecessary のみ) ため
  // 全 field optional.
  diff_json: Record<
    string,
    {
      added?: string[]
      removed?: string[]
      edited?: { from: string; to: string }[]
      marked_unnecessary?: string[]
      deselected?: string[]
      selected?: string[]
    }
  >
  summary: string
  edited_by: string | null
  created_at: string
}

/**
 * 薬歴統計情報の取得
 */
export const getYakurekiStatistics = (): AxiosPromise<YakurekiStatistics> => {
  return client.get('/portal/statistics/')
}
